import React, { useState, useEffect } from 'react';
import { Play, Activity, Users, Music, Brain, ChevronLeft, Search, X, Palette } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import { getStoredTheme, setStoredTheme, applyTheme, THEMES } from './themes/init_theme';

// Import des moteurs de jeu
import QuizGame from './components/QuizGame';
import TimelineGame from './components/TimelineGame';
import ProceduralObservationGame from './components/ProceduralObservationGame';
import DiscussionGame from './components/DiscussionGame';
import SensoryGame from './components/SensoryGame';
import ReflexGame from './components/ReflexGame';
import ThrowingGame from './components/ThrowingGame';
import TracingGame from './components/TracingGame';
import VotingGame from './components/VotingGame';
import ComplimentGame from './components/ComplimentGame';
import BrainstormingGame from './components/BrainstormingGame';

const ICONS = {
  COGNITIVE: Brain,
  LANGUAGE: Activity,
  SENSORY: Music,
  MOTOR_SKILLS: Play,
  SOCIAL: Users
};

// Labels français pour l'affichage
const CATEGORY_LABELS = {
  COGNITIVE: "Cognitif",
  LANGUAGE: "Langage",
  SENSORY: "Sensoriel",
  MOTOR_SKILLS: "Motricité",
  SOCIAL: "Social"
};

function App() {
  const [games, setGames] = useState([]);
  const [currentGame, setCurrentGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(getStoredTheme());

  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme]);

  const toggleTheme = (themeName) => {
    setCurrentTheme(themeName);
    setStoredTheme(themeName);
  };

  useEffect(() => {
    axios.get(`${API_BASE_URL}/games`)
      .then(res => {
        setGames(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Erreur API", err);
        setLoading(false);
      });
  }, []);

  const handleGameEnd = (score) => {
    console.log("Fin de partie, score:", score);
    setCurrentGame(null);
  };

  // --- LOGIQUE DE FILTRAGE ---
  const filteredGames = games.filter(game => {
    const matchesSearch = game.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory ? game.category === selectedCategory : true;
    return matchesSearch && matchesCategory;
  });

  // --- ROUTEUR DE JEUX ---
  const renderGameComponent = () => {
    if (!currentGame) return null;
    const { type } = currentGame.config || {};
    // On injecte le currentTheme dans tous les composants
    const gameProps = { config: currentGame.config, onEnd: handleGameEnd, currentTheme };

    switch (type) {
      case 'quiz':
      case 'binary':
      case 'complete_sentence':
      case 'image_recognition':
      case 'progressive_clue':
      case 'emotion_recognition':
        return <QuizGame {...gameProps} />;
      case 'drag_and_drop_order':
        return <TimelineGame {...gameProps} />;
      case 'visual_search':
      case 'spot_difference':
        return <ProceduralObservationGame {...gameProps} />;
      case 'discussion_starter':
      case 'open_question':
      case 'emotional_sentence':
        return <DiscussionGame {...gameProps} />;
      case 'audio_visual':
      case 'travel_slide':
      case 'music_memory':
        return <SensoryGame {...gameProps} />;
      case 'touch_reflex':
        return <ReflexGame {...gameProps} />;
      case 'drag_and_drop_target':
        return <ThrowingGame {...gameProps} />;
      case 'path_tracing':
        return <TracingGame {...gameProps} />;
      case 'binary_vote':
      case 'photo_vote':
        return <VotingGame {...gameProps} />;
      case 'drag_and_drop_social':
        return <ComplimentGame {...gameProps} />;
      case 'brainstorming':
        return <BrainstormingGame {...gameProps} />;
      default:
        return (
          <div className="status-message">
            <h2>Module en construction 🚧</h2>
            <button onClick={() => setCurrentGame(null)}>Retour</button>
          </div>
        );
    }
  };

  // Affichage du mode Jeu
  if (currentGame) {
    return (
      <div className="game-screen">
        <button className="back-to-home" onClick={() => setCurrentGame(null)}>
          <ChevronLeft /> Retour à l'accueil
        </button>
        <div className="game-content-area">
          {renderGameComponent()}
        </div>
      </div>
    );
  }

  // Affichage de l'accueil
  return (
    <div className="app-layout">
      <div className="top-bar">
        <div className="theme-switcher">
          <Palette size={20} />
          <button
            className={currentTheme === THEMES.CLASSIQUE ? 'active' : ''}
            onClick={() => toggleTheme(THEMES.CLASSIQUE)}
          >
            Classique
          </button>
          <button
            className={currentTheme === THEMES.DYNAMIQUE ? 'active' : ''}
            onClick={() => toggleTheme(THEMES.DYNAMIQUE)}
          >
            Dynamique
          </button>
        </div>
      </div>

      <div className="content-container">
        <header className="hero-header">
          <h1 className="main-title">
            ErgoMind <span className="highlight">Soins+</span>
          </h1>
          <p className="main-subtitle">Activités Thérapeutiques</p>
        </header>

        <div className="controls-row">
          <div className="search-box">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher une activité..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && <X className="clear-icon" onClick={() => setSearchTerm("")} />}
          </div>

          <div className="filter-chips">
            <button
              className={selectedCategory === null ? 'chip active' : 'chip'}
              onClick={() => setSelectedCategory(null)}
            >
              Tout voir
            </button>
            {Object.keys(CATEGORY_LABELS).map(cat => (
              <button
                key={cat}
                className={selectedCategory === cat ? 'chip active' : 'chip'}
                onClick={() => setSelectedCategory(cat === selectedCategory ? null : cat)}
              >
                {CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="loading-container">Chargement...</div>
        ) : filteredGames.length === 0 ? (
          <div className="no-results">Aucune activité trouvée.</div>
        ) : (
          <div className="games-grid">
            {filteredGames.map((game) => {
              const Icon = ICONS[game.category] || Activity;
              return (
                <div key={game.id} className="game-card" onClick={() => setCurrentGame(game)}>
                  <div className="card-icon">
                    <Icon size={48} strokeWidth={1.5} />
                  </div>
                  <h3 className="card-title">{game.title}</h3>
                  <span className="card-tag">{CATEGORY_LABELS[game.category]}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;